import { DateTimeFormatter } from '@wix/bookings-date-time';
import { PriceUtils } from '@wix/bookings-uou-mappers';
import { Experiments } from '@wix/yoshi-flow-editor';
import {
  createBuyPlanAndBookOption,
  createBuyPlanBenefitOption,
  createBuyPlanFromPurchasablePlanOption,
  createBuyPlanOptionPerVariant,
  createCustomPriceOption,
  createDepositOption,
  createFreeOption,
  createFullAmountOption,
  createPayOfflineOption,
  createPayOnlineOption,
  createSubscriptionOption,
  createUsePlanOption,
  getUniquePurchasablePlans,
  isDepositOrFullAmountFeatureEnabled,
  isOfferedAsOneTime,
} from '..';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  PaymentMethod,
  PaymentOption,
  TFunction,
} from '../../../../types/types';
import { isServiceOfferedAsPricingPlan } from '../../../mappers/serviceMapper/utils/payment.mapper';
import { isRuleBasedVariant } from '../../../mappers/dynamic-price.mapper';
import { SlotService } from '../../../state/types';
import { isPlanAndBookEnabled } from '../../plans/utils';

export const getSlotServicePaymentOptions = ({
  slotService,
  numberOfParticipants,
  isPricingPlanInstalled,
  t,
  dateAndTimeFormatter,
  dateRegionalSettingsLocale,
  experiments,
  isCart,
  excludePricingPlanOptions = false,
  isPlanAndBookBlocked,
  isPricingPlansMigratedToEcom,
}: {
  slotService: SlotService;
  numberOfParticipants: number;
  isPricingPlanInstalled: boolean;
  t: TFunction;
  dateAndTimeFormatter: DateTimeFormatter;
  dateRegionalSettingsLocale: string;
  experiments: Experiments;
  isCart: boolean | undefined;
  excludePricingPlanOptions?: boolean;
  /**
   * ECP-12919, booking-level — see bookingRequiresStoredPaymentMethod. Passed in rather than
   * derived from `slotService`, because one blocked service disables Plan & Book for every
   * service in the booking, not just its own.
   */
  isPlanAndBookBlocked?: boolean;
  /** Whether the site's Pricing Plans are migrated to eCom — see isPlanAndBookEnabled. */
  isPricingPlansMigratedToEcom?: boolean;
}): PaymentOption[] => {
  const isSubscription =
    slotService.paymentDetails.subscriptionInfo &&
    experiments.enabled(ExperimentsConsts.SubscriptionPricingUoU);
  if (isSubscription) {
    const formattedPrice = PriceUtils.getFormattedCurrency({
      price: slotService.paymentDetails.price,
      currency: slotService.paymentDetails.currency,
      locale: dateRegionalSettingsLocale,
      currencyDisplay: 'narrowSymbol',
    });
    return [
      createSubscriptionOption(
        t,
        slotService.paymentDetails.subscriptionInfo!.totalPayments,
        formattedPrice,
      ),
    ];
  }

  const options = excludePricingPlanOptions
    ? []
    : slotService.memberships?.eligibleMemberships?.map((membership) =>
        createUsePlanOption(
          membership,
          numberOfParticipants,
          t,
          dateAndTimeFormatter,
          undefined,
        ),
      ) ?? [];

  // add invalid options if at least one option is eligible
  if (
    !excludePricingPlanOptions &&
    options.length > 0 &&
    slotService.memberships?.invalidMemberships?.length
  ) {
    options.push(
      ...slotService.memberships.invalidMemberships
        .filter((membership) => membership.membership?.credits?.remaining !== 0)
        .map((membership) =>
          createUsePlanOption(
            membership.membership!,
            numberOfParticipants,
            t,
            dateAndTimeFormatter,
            membership.reason,
          ),
        ),
    );
  }

  const showBuyAPricingPlan = isServiceOfferedAsPricingPlan(
    slotService.service.payment,
    isPricingPlanInstalled,
  );

  if (
    !excludePricingPlanOptions &&
    options.length === 0 &&
    showBuyAPricingPlan
  ) {
    const planAndBookEnabled = isPlanAndBookEnabled({
      experiments,
      isPricingPlansMigratedToEcom,
    });
    if (planAndBookEnabled && isPlanAndBookBlocked) {
      // `eligiblePlans` (the pre-Plan & Book "Buy a plan" source) isn't
      // fetched once Plan & Book is on (see fetchInitialData), so build the
      // same per-plan "Buy a plan" list from the purchasable-plan data we do
      // have instead.
      options.push(
        ...getUniquePurchasablePlans(
          slotService.purchasableBenefits,
          experiments,
        ).map((plan) =>
          createBuyPlanFromPurchasablePlanOption(plan, experiments),
        ),
      );
    } else if (planAndBookEnabled) {
      // Plan & Book: surface purchasable plans as "buy plan & book" options,
      // one per covering benefit, replacing the legacy "Buy a plan" entries.
      options.push(
        ...(slotService.purchasableBenefits?.map(
          ({ plan, benefit, redemptionCost }) =>
            createBuyPlanAndBookOption({
              plan,
              benefit,
              redemptionCost,
              numberOfParticipants,
              experiments,
              t,
            }),
        ) ?? []),
      );
    } else if (
      experiments.enabled(ExperimentsConsts.PricingPlansMultiplePricingVariants)
    ) {
      options.push(
        ...(slotService.eligiblePlans?.flatMap(createBuyPlanOptionPerVariant) ??
          []),
      );
    } else {
      options.push(
        ...(slotService.eligiblePlans?.map(createBuyPlanBenefitOption) ?? []),
      );
    }
  }
  const showPaySingleSession = isOfferedAsOneTime(slotService.service.payment);

  if (showPaySingleSession) {
    let price = slotService.paymentDetails.price;
    const selectedVariant = slotService.dynamicPriceInfo?.selectedVariants?.[0];
    if (selectedVariant && !isRuleBasedVariant(selectedVariant)) {
      const priceString =
        slotService.dynamicPriceInfo?.serviceOptionsAndVariants?.variants?.values?.find(
          (value) =>
            value.choices?.find(
              (choice) =>
                (choice.custom &&
                  choice.custom === selectedVariant?.choices?.[0]?.custom) ||
                (choice.staffMemberId &&
                  choice.staffMemberId ===
                    selectedVariant?.choices?.[0]?.staffMemberId) ||
                (choice.duration &&
                  choice.duration.minutes ===
                    selectedVariant?.choices?.[0]?.duration?.minutes),
            ),
        )?.price?.value!;

      price = Number(priceString);
    }

    // When an automatic discount applies, the Pay Now option shows the catalog
    // (pre-discount) price — the discount itself is applied at checkout. Since
    // `paymentDetails.price` already reflects the discounted amount, fall back to
    // `priceBeforeDiscount` for display whenever a discount rule is present.
    const { discountRuleName, priceBeforeDiscount } =
      slotService.paymentDetails;
    const displayPrice =
      discountRuleName && Number.isFinite(priceBeforeDiscount)
        ? priceBeforeDiscount!
        : price;

    const deposit = slotService.paymentDetails.minCharge;
    const isDepositeOrFullAmountEnabled = isDepositOrFullAmountFeatureEnabled({
      experiments,
      fullUpfrontPaymentAllowed:
        slotService.paymentDetails.fullUpfrontPaymentAllowed,
      deposit,
      paymentTypes: slotService.service.paymentTypes,
    });

    if (isDepositeOrFullAmountEnabled) {
      // Full amount shows the catalog (pre-discount) price in the suffix, like
      // the Pay Now option; the deposit shows the deposit amount itself.
      const formattedFullPrice = PriceUtils.getFormattedCurrency({
        price: displayPrice,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      const formattedDeposit = PriceUtils.getFormattedCurrency({
        price: deposit,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      options.push(
        createDepositOption(formattedDeposit, t, discountRuleName, isCart),
      );
      options.push(
        createFullAmountOption(formattedFullPrice, t, discountRuleName, isCart),
      );
    } else if (
      slotService.service.paymentTypes.includes(PaymentMethod.ONLINE)
    ) {
      const formattedPrice = PriceUtils.getFormattedCurrency({
        price: displayPrice,
        currency: slotService.paymentDetails.currency,
        locale: dateRegionalSettingsLocale,
        currencyDisplay: 'narrowSymbol',
      });
      options.push(
        createPayOnlineOption(formattedPrice, t, discountRuleName, isCart),
      );
    }
    if (slotService.service.paymentTypes.includes(PaymentMethod.OFFLINE)) {
      const isFree = slotService.service.payment.paymentDetails.isFree;
      const customPrice = slotService.service.payment.paymentDetails.priceText;
      if (isFree) {
        if (customPrice) {
          options.push(createCustomPriceOption(customPrice));
        } else {
          options.push(createFreeOption());
        }
      } else {
        options.push(
          createPayOfflineOption(
            PriceUtils.getFormattedCurrency({
              price: displayPrice,
              currency: slotService.paymentDetails.currency,
              locale: dateRegionalSettingsLocale,
              currencyDisplay: 'narrowSymbol',
            }),
            t,
            discountRuleName,
            isCart,
          ),
        );
      }
    }
  }

  if (slotService.paymentDetails.discountCalculatedAtCheckout) {
    return options.map((option) =>
      option.discountDetails
        ? {
            ...option,
            discountDetails: {
              ...option.discountDetails,
              calculatedAtCheckout: true,
            },
          }
        : option,
    );
  }

  return options;
};
